// Public read-only client portal — /portal/client/:token
// No auth required. Serves a sanitized subset from GET /api/client-pages/portal/:token.
// Clients (Mario, Leslie, etc.) can bookmark this URL and check status
// on their properties without a Field Radar login.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Buildings, User as UserIcon, MapPin, Wrench, CheckCircle, Plus, X as XIcon } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_COLOR = {
  pool: "bg-amber-100 text-amber-800 border-amber-300",
  assigned: "bg-sky-100 text-sky-800 border-sky-300",
  in_progress: "bg-violet-100 text-violet-800 border-violet-300",
  follow_up: "bg-orange-100 text-orange-800 border-orange-300",
  needs_quote: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
};

export default function ClientPortalPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound
  const [submitPick, setSubmitPick] = useState("");
  const [propsOpen, setPropsOpen] = useState(false);
  const brand = data?.brand_color || "";
  const logoUrl = data?.has_logo
    ? `${process.env.REACT_APP_BACKEND_URL}/api/client-pages/portal/${token}/logo`
    : null;

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/client-pages/portal/${token}`);
        setData(r.data);
        setState("ok");
      } catch {
        setState("notfound");
      }
    })();
  }, [token]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500 font-bold uppercase text-xs">
        Loading…
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6" data-testid="portal-notfound">
        <div className="text-6xl mb-4">🔒</div>
        <div className="font-display font-black text-2xl text-slate-800 uppercase tracking-tight">Link Expired</div>
        <div className="mt-2 text-sm text-slate-500 max-w-md text-center">
          This portal link is no longer active. Ask whoever shared it to send you a new link.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-page">
      <header
        className="text-white"
        style={brand
          ? { background: `linear-gradient(135deg, ${brand}, ${brand}dd 55%, #020617)` }
          : undefined}
        {...(!brand ? { "data-default-brand": "1" } : {})}>
        <div
          className={brand ? "" : "bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-950"}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/70 mb-1">
              Field Radar · Client Portal
            </div>
            <div className="flex items-start gap-3">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  data-testid="portal-brand-logo"
                  className="w-12 h-12 rounded bg-white/95 p-1 object-contain flex-shrink-0"/>
              ) : (
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  data.is_org ? "bg-violet-500/20 text-violet-200" : "bg-emerald-500/20 text-emerald-200"
                }`}>
                  {data.is_org ? <Buildings weight="fill" size={22}/> : <UserIcon weight="fill" size={22}/>}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="font-display font-black text-3xl sm:text-4xl leading-none tracking-tight">
                  {data.name}
                </h1>
                {data.org_name && !data.is_org && (
                  <div className="mt-1 text-sm text-white/70">{data.org_name}</div>
                )}
                {data.role && <div className="text-xs text-white/60 italic">{data.role}</div>}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Submit new WO — compact dropdown picker */}
        {data.linked_properties.some(p => p.submit_wo_path) && (
          <section
            className={`border-2 rounded-lg p-4 ${brand ? "" : "bg-emerald-50 border-emerald-300"}`}
            style={brand ? { background: `${brand}12`, borderColor: brand } : undefined}
            data-testid="portal-submit-new">
            <div className="flex items-center gap-2 mb-2">
              <Plus size={16} style={brand ? { color: brand } : undefined} className={brand ? "" : "text-emerald-700"}/>
              <div
                className="font-black uppercase text-[11px] tracking-widest"
                style={brand ? { color: brand } : undefined}>
                <span className={brand ? "" : "text-emerald-900"}>Submit a New Work Order</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={submitPick}
                onChange={e => setSubmitPick(e.target.value)}
                data-testid="portal-submit-picker"
                style={brand ? { borderColor: brand } : undefined}
                className={`flex-1 h-10 px-2 border-2 text-sm bg-white ${brand ? "" : "border-emerald-300"}`}>
                <option value="">— Pick a unit —</option>
                {data.linked_properties
                  .filter(p => p.submit_wo_path)
                  .map((p, i) => (
                    <option key={i} value={p.submit_wo_path}>
                      {p.unit ? `Unit ${p.unit} · ${p.address}` : p.address}
                    </option>
                  ))}
              </select>
              <a
                href={submitPick || "#"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { if (!submitPick) { e.preventDefault(); toast.error("Pick a unit first"); } }}
                data-testid="portal-submit-open"
                style={submitPick && brand ? { background: brand } : undefined}
                className={`h-10 px-4 inline-flex items-center justify-center gap-1 font-black uppercase text-[11px] tracking-widest whitespace-nowrap ${
                  submitPick
                    ? (brand ? "text-white hover:brightness-110" : "bg-emerald-600 hover:bg-emerald-700 text-white")
                    : "bg-slate-200 text-slate-400 cursor-not-allowed"
                }`}>
                <Plus size={11}/> Open Form
              </a>
            </div>
            {/* Selected-unit confirmation strip — helps users visually verify
                the pick before opening the WO form. */}
            {submitPick && (() => {
              const picked = data.linked_properties.find(p => p.submit_wo_path === submitPick);
              if (!picked) return null;
              return (
                <div
                  data-testid="portal-submit-selected"
                  style={brand ? { borderColor: brand } : undefined}
                  className={`mt-2 flex items-center gap-2 px-3 py-2 bg-white border-2 rounded ${brand ? "" : "border-emerald-500"}`}>
                  <CheckCircle
                    size={14} weight="fill"
                    style={brand ? { color: brand } : undefined}
                    className={`${brand ? "" : "text-emerald-600"} flex-shrink-0`}/>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[9px] font-black uppercase tracking-widest"
                      style={brand ? { color: brand } : undefined}>
                      <span className={brand ? "" : "text-emerald-700"}>Submitting for</span>
                    </div>
                    <div className="text-sm font-black text-slate-900 truncate">
                      {picked.unit ? `Unit ${picked.unit}` : "Property"} · {picked.address}
                    </div>
                  </div>
                </div>
              );
            })()}
          </section>
        )}

        {/* Open WOs (now above Properties) */}
        <section className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid="portal-open-wos">
          <div className="flex items-center gap-2 mb-3">
            <Wrench size={14} className="text-slate-500"/>
            <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
              Open Work Orders · {data.open_wos.length}
            </div>
          </div>
          {data.open_wos.length === 0 ? (
            <div className="text-xs text-slate-400 italic py-2 text-center">
              🎉 No open work orders right now.
            </div>
          ) : (
            <div className="space-y-1.5">
              {data.open_wos.map((j, i) => (
                <div key={j.id || i} className="flex items-start gap-2 px-2 py-1.5 border border-slate-100 rounded"
                  data-testid={`portal-open-wo-${i}`}>
                  <span className={`inline-flex items-center px-1.5 py-0.5 border text-[9px] font-black uppercase tracking-widest whitespace-nowrap ${STATUS_COLOR[j.status] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
                    {j.status}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-[12px] text-slate-900 truncate">{j.title}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {j.address}{j.unit ? ` · ${j.unit}` : ""}
                      {j.work_order_number && <span className="ml-2 font-mono">WO#{j.work_order_number}</span>}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      if (!confirm(`Cancel "${j.title}"? This immediately closes the work order.`)) return;
                      try {
                        await api.post(`/client-pages/portal/${token}/cancel/${j.id}`, {});
                        toast.success("Cancelled");
                        // reload
                        const r = await api.get(`/client-pages/portal/${token}`);
                        setData(r.data);
                      } catch (e) {
                        toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Cancel failed"));
                      }
                    }}
                    data-testid={`portal-cancel-wo-${j.id}`}
                    className="flex-shrink-0 opacity-60 hover:opacity-100 h-6 w-6 flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded"
                    title="Cancel this work order">
                    <XIcon size={14}/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recent completed */}
        <section className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid="portal-recent-completed">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={14} className="text-emerald-600"/>
            <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
              Recently Completed · {data.recent_completed.length}
            </div>
          </div>
          {data.recent_completed.length === 0 ? (
            <div className="text-xs text-slate-400 italic py-2 text-center">Nothing completed recently.</div>
          ) : (
            <div className="space-y-1.5">
              {data.recent_completed.map((j, i) => (
                <div key={i} className="flex items-start gap-2 px-2 py-1.5 border border-slate-100 rounded"
                  data-testid={`portal-completed-wo-${i}`}>
                  <CheckCircle size={12} className="text-emerald-500 mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-slate-800 truncate">{j.title}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {j.address}{j.unit ? ` · ${j.unit}` : ""}
                      {j.completed_at && (
                        <span className="ml-2">· {new Date(j.completed_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Linked properties (compact — expand on demand) */}
        <section className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid="portal-properties">
          <button
            onClick={() => setPropsOpen(v => !v)}
            data-testid="portal-properties-toggle"
            className="w-full flex items-center gap-2 text-left">
            <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
              Properties · {data.linked_properties.length}
            </div>
            <div className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-400">
              {propsOpen ? "Hide" : "Show all"}
            </div>
          </button>
          {propsOpen && (
            <div className="mt-3 space-y-1 max-h-72 overflow-y-auto">
              {data.linked_properties.length === 0 ? (
                <div className="text-xs text-slate-400 italic">No properties linked.</div>
              ) : data.linked_properties.map((p, i) => (
                <div key={i} className="flex items-center gap-2 px-2 py-1.5 border border-slate-100 rounded text-xs">
                  <MapPin size={12} className="text-slate-400 flex-shrink-0"/>
                  <span className="font-black">{p.address}</span>
                  {p.unit && <span className="text-slate-500">· Unit {p.unit}</span>}
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="text-center text-[10px] text-slate-400 pt-4">
          Powered by Field Radar · Read-only view · Refresh to see latest
        </div>
      </main>
    </div>
  );
}
